import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowClockwise } from '@phosphor-icons/react';
import { hapticTap, isNativeApp, isNativeTabRoot } from '../../lib/native';
import NativeTopBar from './NativeTopBar';
import NativeTabBar from './NativeTabBar';

const PULL_TRIGGER_PX = 80;
const PULL_MAX_PX = 120;

const NativeAppShell = ({ user, setUser, children }) => {
  const native = isNativeApp();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const contentRef = useRef(null);
  const pull = useRef(null);
  const [pullPx, setPullPx] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!native) return undefined;
    let listener;
    let cancelled = false;
    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        listener = await App.addListener('backButton', ({ canGoBack }) => {
          if (cancelled) return;
          // An open dialog/sheet closes first (Radix closes on Escape).
          const dialog = document.querySelector('[role="dialog"]');
          if (dialog) {
            dialog.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
            return;
          }
          if (isNativeTabRoot(window.location.pathname) || !canGoBack) {
            App.minimizeApp();
            return;
          }
          navigate(-1);
        });
      } catch {
        /* browser */
      }
    })();
    return () => {
      cancelled = true;
      if (listener && listener.remove) listener.remove();
    };
  }, [native, navigate]);

  // New screen: start at the top, replay the enter animation, move focus to
  // the page so screen readers announce the new screen.
  useEffect(() => {
    if (!native) return;
    window.scrollTo(0, 0);
    const el = contentRef.current;
    if (!el) return;
    el.classList.remove('native-page-enter');
    void el.offsetWidth; // restart the CSS animation
    el.classList.add('native-page-enter');
    el.focus({ preventScroll: true });
  }, [native, pathname]);

  if (!native) return children;

  const canPull = isNativeTabRoot(pathname);

  const onTouchStart = (e) => {
    if (!canPull || refreshing || window.scrollY > 0) return;
    pull.current = { y: e.touches[0].clientY, x: e.touches[0].clientX };
  };
  const onTouchMove = (e) => {
    if (!pull.current) return;
    const dy = e.touches[0].clientY - pull.current.y;
    const dx = Math.abs(e.touches[0].clientX - pull.current.x);
    if (dy <= 0 || dx > dy || window.scrollY > 0) { setPullPx(0); return; }
    setPullPx(Math.min(PULL_MAX_PX, dy * 0.5));
  };
  const onTouchEnd = () => {
    if (!pull.current) return;
    pull.current = null;
    if (pullPx >= PULL_TRIGGER_PX) {
      hapticTap();
      setRefreshing(true);
      window.location.reload();
      return;
    }
    setPullPx(0);
  };

  return (
    <div className="native-shell">
      <a href="#main-content" className="native-skip-link">Skip to content</a>
      <NativeTopBar user={user} setUser={setUser} />
      {canPull && (pullPx > 0 || refreshing) && (
        <div
          className="native-ptr"
          style={{ height: refreshing ? 56 : pullPx }}
          role="status"
          aria-live="polite"
        >
          <ArrowClockwise
            size={22}
            weight="bold"
            className={refreshing ? 'native-ptr-spin' : ''}
            style={{ transform: refreshing ? undefined : `rotate(${pullPx * 3}deg)`, opacity: Math.min(1, pullPx / PULL_TRIGGER_PX) }}
          />
          <span className="sr-only">{refreshing ? 'Refreshing' : pullPx >= PULL_TRIGGER_PX ? 'Release to refresh' : 'Pull to refresh'}</span>
        </div>
      )}
      <main
        id="main-content"
        ref={contentRef}
        tabIndex={-1}
        className="native-shell-content"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {children}
      </main>
      <NativeTabBar user={user} />
    </div>
  );
};

export default NativeAppShell;
